import { ArrowDown, ArrowUp, Plus, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThinkerDisposition } from "~/api/conversationTypes";
import {
  useAddConversationThinker,
  useAvailableThinkers,
  useConversationThinkers,
  useRemoveConversationThinker,
  useSetConversationThinkers,
} from "~/api/queries";
import { ThinkerPicker } from "~/features/thinkers/ThinkerPicker";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Text } from "~/foundation/components/Text";

/**
 * Who answers, and in what order (W8 §B.4).
 *
 * The removal dialog is the important part. `DELETE .../thinkers/{id}` defaults to
 * `action:"delete"`, which **hard-deletes every message that thinker authored** (W8-3) — so
 * removal always asks, the destructive option is never the default, and the answer's
 * `messagesAffected` is reported rather than swallowed.
 */
export function ThinkerPanel({
  conversationId,
  documentId,
  canUpdate,
}: {
  conversationId: string;
  documentId: string;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const { data: assigned } = useConversationThinkers(conversationId);
  const { data: available } = useAvailableThinkers();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [action, setAction] = useState<ThinkerDisposition["action"]>("merge");
  const [mergeTo, setMergeTo] = useState("");
  const [outcome, setOutcome] = useState<string | null>(null);

  const add = useAddConversationThinker();
  const reorder = useSetConversationThinkers();
  const remove = useRemoveConversationThinker();

  const ids = assigned ?? [];
  const nameOf = (id: string) => (available ?? []).find((entry) => entry.id === id)?.name ?? id;

  const move = (index: number, delta: number) => {
    const next = [...ids];
    const target = index + delta;
    const current = next[index];
    const swap = next[target];
    if (current === undefined || swap === undefined) {
      return;
    }
    next[index] = swap;
    next[target] = current;
    reorder.mutate({ id: conversationId, thinkerIds: next });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <Text variant="small" className="text-text-secondary">
          {t("Thinkers answer in this order.")}
        </Text>
        <Button
          size="sm"
          icon={Plus}
          className="ms-auto"
          disabled={!canUpdate}
          onClick={() => setPickerOpen(true)}
        >
          {t("Add")}
        </Button>
      </div>

      {ids.length === 0 ? <Empty message={t("No thinkers on this conversation yet.")} /> : null}

      <ul className="flex flex-col gap-1">
        {ids.map((id, index) => (
          <li
            key={id}
            className="flex items-center gap-2 rounded-sm border border-divider bg-bg p-2"
          >
            <span className="min-w-0 flex-1 truncate text-body text-text">{nameOf(id)}</span>
            <IconButton
              icon={ArrowUp}
              size="sm"
              aria-label={t("Move up")}
              disabled={!canUpdate || index === 0}
              onClick={() => move(index, -1)}
            />
            <IconButton
              icon={ArrowDown}
              size="sm"
              aria-label={t("Move down")}
              disabled={!canUpdate || index === ids.length - 1}
              onClick={() => move(index, 1)}
            />
            <IconButton
              icon={Trash}
              size="sm"
              aria-label={t("Remove thinker")}
              disabled={!canUpdate}
              onClick={() => {
                setRemoving(id);
                setAction("merge");
                setMergeTo(ids.find((entry) => entry !== id) ?? "");
              }}
            />
          </li>
        ))}
      </ul>

      {outcome ? (
        <Text variant="small" className="text-text-tertiary">
          {outcome}
        </Text>
      ) : null}

      <ThinkerPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={ids}
        onSelect={(next) => {
          for (const id of next) {
            if (!ids.includes(id)) {
              add.mutate({ id: conversationId, thinkerId: id });
            }
          }
        }}
        multiple
        filter="ai"
        documentId={documentId}
        title={t("Add a thinker")}
        confirmLabel={t("Add")}
      />

      <Dialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null);
          }
        }}
        title={t("Remove thinker")}
        description={
          removing
            ? t("What would you like to do with the responses from {{name}}?", {
                name: nameOf(removing),
              })
            : undefined
        }
        closeLabel={t("Close")}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              {t("Cancel")}
            </Button>
            <Button
              variant={action === "delete" ? "danger" : "primary"}
              disabled={remove.isPending || (action === "merge" && !mergeTo)}
              onClick={() => {
                if (!removing) {
                  return;
                }
                remove.mutate(
                  {
                    id: conversationId,
                    thinkerId: removing,
                    disposition:
                      action === "merge"
                        ? { action: "merge", mergeToThinkerId: mergeTo }
                        : { action: "delete" },
                  },
                  {
                    onSuccess: (result) => {
                      setOutcome(
                        result.action === "delete"
                          ? t("Thinker removed. {{count}} messages deleted.", {
                              count: result.messagesAffected,
                            })
                          : t("Thinker removed. {{count}} messages merged.", {
                              count: result.messagesAffected,
                            }),
                      );
                      setRemoving(null);
                    },
                  },
                );
              }}
            >
              {action === "delete" ? t("Delete responses") : t("Merge responses")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="thinker-disposition"
              checked={action === "merge"}
              onChange={() => setAction("merge")}
            />
            <span className="text-body text-text">{t("Merge responses to another thinker")}</span>
          </label>
          {action === "merge" ? (
            <select
              value={mergeTo}
              onChange={(event) => setMergeTo(event.target.value)}
              aria-label={t("Merge into")}
              className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
            >
              <option value="">{t("Select a thinker…")}</option>
              {ids
                .filter((id) => id !== removing)
                .map((id) => (
                  <option key={id} value={id}>
                    {nameOf(id)}
                  </option>
                ))}
            </select>
          ) : null}
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="thinker-disposition"
              checked={action === "delete"}
              onChange={() => setAction("delete")}
            />
            <span className="text-body text-text">
              {t("Delete all responses from this thinker")}
            </span>
          </label>
          <Text variant="small" className="text-warning">
            {t("Deleting responses cannot be undone.")}
          </Text>
        </div>
      </Dialog>
    </div>
  );
}
